import type {VehicleListStatusBadge} from "@/src/modules/vehicles/lib/vehicle-list-amenity-badges";
import {moment} from "@/src/modules/core/lib";

import type {
  AuthorizationCoverage,
  VehicleCheck,
  VehicleCheckAuthorizationSnapshot,
} from "../types";

const AT_SCAN_TOOLTIP = "Authorization at time of scan";

/** @deprecated API may still return `authorized` on older checks; normalized to `active`. */
const LEGACY_AUTHORIZED_COVERAGE = "authorized";

export function getAuthorizationCoverageDisplay(
  coverage: AuthorizationCoverage | null | undefined,
): VehicleListStatusBadge | null {
  if (!coverage || coverage === "none") return null;

  const normalizedCoverage =
    (coverage as string) === LEGACY_AUTHORIZED_COVERAGE ? "active" : coverage;

  const displays: Record<Exclude<AuthorizationCoverage, "none">, VehicleListStatusBadge> = {
    active: {
      label: "Active",
      variant: "success",
      tooltip: AT_SCAN_TOOLTIP,
    },
    suspended: {
      label: "Suspended",
      variant: "warning",
      tooltip: AT_SCAN_TOOLTIP,
    },
    household_restricted: {
      label: "Restricted",
      variant: "warning",
      tooltip: AT_SCAN_TOOLTIP,
    },
    outside_hours: {
      label: "Outside hours",
      variant: "warning",
      tooltip: "Safelisting not active at scan time",
    },
    scheduled: {
      label: "Not started",
      variant: "warning",
      tooltip: "Permit not yet active at scan time",
    },
    recently_expired: {
      label: "Recently expired",
      variant: "warning",
      tooltip: AT_SCAN_TOOLTIP,
    },
    expired: {
      label: "Expired",
      variant: "secondary",
      tooltip: AT_SCAN_TOOLTIP,
    },
  };

  return (
    displays[normalizedCoverage as Exclude<AuthorizationCoverage, "none">] ?? {
      label: normalizedCoverage,
      variant: "secondary",
      tooltip: AT_SCAN_TOOLTIP,
    }
  );
}

export function getAuthorizationPlaceholderDisplay(): VehicleListStatusBadge {
  return {label: "—", variant: "secondary"};
}

export function shouldShowCheckAuthorizationForVehicle(
  check: VehicleCheck | null | undefined,
  vehicleId: number,
): boolean {
  if (!check) return false;
  const resolvedId = check.vehicle_id ?? check.suggested_vehicle_id;
  return resolvedId === vehicleId;
}

export function formatSnapshotDateTime(
  value: string | null | undefined,
  propertyTimezone?: string | null,
): string {
  if (!value) return "—";
  const m = propertyTimezone ? moment.utc(value).tz(propertyTimezone) : moment.utc(value).local();
  return m.isValid() ? m.format("MMM D, YYYY h:mm A") : value;
}

export function formatSnapshotDate(
  value: string | null | undefined,
  propertyTimezone?: string | null,
): string {
  if (!value) return "—";
  const m = propertyTimezone
    ? moment.tz(value, "YYYY-MM-DD", propertyTimezone)
    : moment(value, "YYYY-MM-DD");
  return m.isValid() ? m.format("MMM D, YYYY") : value;
}

export function hasAuthorizationData(check: VehicleCheck): boolean {
  return (
    check.safelisting_coverage_at_check !== "none" ||
    check.permanent_parking_coverage_at_check !== "none" ||
    check.authorization_snapshot != null
  );
}

export type AuthorizationDetailRow = {
  label: string;
  value: string;
};

export function getAuthorizationDetailRows(
  snapshot: VehicleCheckAuthorizationSnapshot | null | undefined,
  propertyTimezone?: string | null,
  options?: {omitCoverageStatus?: boolean},
): AuthorizationDetailRow[] {
  if (!snapshot) return [];

  const rows: AuthorizationDetailRow[] = [
    {
      label: "Evaluated at",
      value: formatSnapshotDateTime(snapshot.evaluated_at, propertyTimezone),
    },
  ];

  if (snapshot.safelisting) {
    const sl = snapshot.safelisting;
    if (!options?.omitCoverageStatus) {
      rows.push({
        label: "Safelisting status",
        value: getAuthorizationCoverageDisplay(sl.coverage_at_check)?.label ?? sl.coverage_at_check,
      });
    }
    if (sl.day) {
      rows.push({
        label: "Safelisting day",
        value: formatSnapshotDate(sl.day.date, propertyTimezone),
      });
      rows.push({
        label: "Window start",
        value: formatSnapshotDateTime(sl.day.start_datetime, propertyTimezone),
      });
      rows.push({
        label: "Window end",
        value: formatSnapshotDateTime(sl.day.end_datetime, propertyTimezone),
      });
    }
    if (sl.household_restricted && sl.household_amenity_access_restriction_id) {
      rows.push({
        label: "Restriction ID",
        value: String(sl.household_amenity_access_restriction_id),
      });
    }
  }

  if (snapshot.permanent_parking) {
    const pp = snapshot.permanent_parking;
    if (!options?.omitCoverageStatus) {
      rows.push({
        label: "Permanent parking status",
        value: getAuthorizationCoverageDisplay(pp.coverage_at_check)?.label ?? pp.coverage_at_check,
      });
    }
    if (pp.permit_number) {
      rows.push({label: "Permit number", value: pp.permit_number});
    }
    rows.push({
      label: "Permit start",
      value: formatSnapshotDate(pp.start_date, propertyTimezone),
    });
    rows.push({
      label: "Permit end",
      value: formatSnapshotDate(pp.end_date, propertyTimezone),
    });
    if (pp.household_restricted && pp.household_amenity_access_restriction_id) {
      rows.push({
        label: "Restriction ID",
        value: String(pp.household_amenity_access_restriction_id),
      });
    }
  }

  return rows;
}
